/* Oléa analytics collector. Load with defer on the storefront before interaction.
 * The server owns AI measurements. This file never sends customer IDs, email,
 * names, raw chat text, tokens, images, query strings, or album names.
 * The browser never reads or sends an IP address. After statistics consent,
 * the server may observe the request IP and approximate location (30-day retention).
 * No analytics storage or requests until Shopify permits analytics processing.
 */
(() => {
  'use strict';
  if (window.OleaAnalytics) return;
  const ENDPOINT = 'https://olea-ai-luna.netlify.app/api/analytics';
  const IDENTITY_ENDPOINT = 'https://olea-ai-luna.netlify.app/api/analytics-identity';
  const VISITOR_KEY = 'olea.analytics.visitor.v1';
  const SESSION_KEY = 'olea.analytics.session.v1';
  const CONVERSATION_KEY = 'olea.analytics.conversation.v1';
  const IDLE_MS = 30 * 60 * 1000;
  const VERSION = '2.3.0';
  const DESIGN_MODE = window.Shopify?.designMode === true;
  const pageUrl = (() => { try { return new URL(location.href || location.pathname || '/', location.origin); } catch { return null; } })();
  const PREVIEW = ['unpublished', 'development'].includes(window.Shopify?.theme?.role) || pageUrl?.searchParams.has('preview_theme_id') === true;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const BROWSER_EVENTS = new Set(['session_started', 'page_viewed', 'product_viewed', 'search_submitted', 'search_empty', 'favorite_added', 'favorite_removed', 'favorites_imported', 'favorites_snapshot_removed', 'favorites_snapshot_observed', 'ai_recommendation_viewed', 'ai_recommendation_clicked', 'ai_feedback_submitted', 'cart_added', 'consent_updated']);
  let ids = null, conversationId = null, allowedPreviously = false, eventClock = 0;
  let baseline = null, queue = [], timer = null, sending = false, pageRecorded = false, inFlightFlush = null;
  let importOwner = null, mutationSource = null;
  let identityAttempt = null, lastIdentityKey = null, lastIdentityAt = 0, identityGeneration = 0;
  let emptySearchRecorded = false;
  const diagnostics = { last_error: null, last_delivery_at: null, delivered: 0, rejected: 0, favorite_actions: 0, ai_requests: 0,
    privacy_load_attempts: 0, privacy_load_completed: false, privacy_load_error: null };
  let privacyLoading = false, lastPrivacyLoad = 0;
  let consentChoicePending = false, consentChoiceMessage = '', consentChoiceSequence = 0, consentReturnFocus = null;
  const productCache = new Map();
  const uuid = () => window.crypto.randomUUID();
  const read = (storage, key) => { try { return JSON.parse(storage.getItem(key) || 'null'); } catch { return null; } };
  const save = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)); } catch {} };
  function permitted() {
    try { return !DESIGN_MODE && pageUrl?.protocol === 'https:' && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
    catch { return false; }
  }
  function forget() {
    queue = []; ids = null; conversationId = null; baseline = null; importOwner = null; pageRecorded = false; emptySearchRecorded = false;
    identityGeneration++; identityAttempt = null; lastIdentityKey = null; lastIdentityAt = 0;
    clearTimeout(timer); timer = null;
    try { localStorage.removeItem(VISITOR_KEY); } catch {}
    try { sessionStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(CONVERSATION_KEY); } catch {}
  }
  function identity() {
    if (!permitted()) return null;
    const now = Date.now();
    if (!ids || now - ids.lastSeen > IDLE_MS) {
      const savedVisitor = read(localStorage, VISITOR_KEY);
      const visitor = UUID.test(savedVisitor?.id || '') ? savedVisitor.id : uuid();
      const savedSession = read(sessionStorage, SESSION_KEY);
      const continuing = UUID.test(savedSession?.id || '') && Number.isFinite(savedSession.lastSeen) && now >= savedSession.lastSeen && now - savedSession.lastSeen <= IDLE_MS;
      ids = { visitor_id: visitor, session_id: continuing ? savedSession.id : uuid(), lastSeen: now, fresh: !continuing };
      save(localStorage, VISITOR_KEY, { id: visitor });
      conversationId = null;
    }
    ids.lastSeen = now;
    save(sessionStorage, SESSION_KEY, { id: ids.session_id, lastSeen: now });
    return ids;
  }
  function conversation({ fresh = false } = {}) {
    if (!identity()) return null;
    if (fresh || !conversationId) {
      const saved = read(sessionStorage, CONVERSATION_KEY);
      conversationId = !fresh && saved?.session_id === ids.session_id && UUID.test(saved.id || '') ? saved.id : uuid();
      save(sessionStorage, CONVERSATION_KEY, { id: conversationId, session_id: ids.session_id });
    }
    return conversationId;
  }
  function context() {
    const current = identity();
    return current ? { allowed: true, visitor_id: current.visitor_id, session_id: current.session_id, conversation_id: conversation(),
      theme_id: String(window.Shopify?.theme?.id || ''), theme_role: String(window.Shopify?.theme?.role || ''), preview: PREVIEW, collector_version: VERSION } : { allowed: false };
  }
  async function verifyCustomerIdentity() {
    if (!permitted() || location.origin !== 'https://heyolea.com') return false;
    const portal = read(sessionStorage, 'olea.portal.customer.session.v2.ksazx7-h1');
    const customerConfig = document.getElementById('OleaCustomerConfig')?.dataset;
    if (customerConfig?.opShopDomain !== 'ksazx7-h1.myshopify.com' || !customerConfig.opClientId || portal?.clientId !== customerConfig.opClientId || portal?.v !== 2 || portal?.shopDomain !== 'ksazx7-h1.myshopify.com' || portal?.issuer !== 'https://shopify.com/authentication/100469309773') return false;
    if (portal?.origin !== 'https://heyolea.com' || !Number.isFinite(portal.expiresAt) || portal.expiresAt <= Date.now() + 30000 ||
      typeof portal.accessToken !== 'string' || portal.accessToken.length < 16 || portal.accessToken.length > 8192) return false;
    const current = identity();
    if (!current) return false;
    // The key stays in memory. Neither token nor server identity is exposed in
    // analytics events. Shopify verification takes place only on the server.
    const key = current.visitor_id + ':' + current.session_id + ':' + portal.accessToken;
    if (lastIdentityKey === key && Date.now() - lastIdentityAt < 30000) return identityAttempt || false;
    const generation = identityGeneration;
    lastIdentityKey = key; lastIdentityAt = Date.now();
    identityAttempt = (async () => {
      try {
        const response = await fetch(IDENTITY_ENDPOINT, { method: 'POST', mode: 'cors', credentials: 'omit', redirect: 'error',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ visitor_id: current.visitor_id,
            session_id: current.session_id, access_token: portal.accessToken }), signal: AbortSignal.timeout(3000) });
        return generation === identityGeneration && permitted() && response.ok;
      } catch { return false; }
    })();
    return identityAttempt;
  }
  function cohort() {
    // UI hint only. Never interpreted by the server as an authenticated identity.
    return document.querySelector('#OleaCustomerConfig')?.dataset.opAuthenticated === 'true' ? 'signed_in_unverified' : 'anonymous';
  }
  function commonProperties() {
    // Derive broad classes locally; never send the user-agent or IP address.
    const navigator = window.navigator || {}, ua = String(navigator.userAgent || '');
    const tablet = /iPad|Tablet|Silk|Android(?!.*Mobile)/i.test(ua) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const mobile = navigator.userAgentData?.mobile === true || /Mobi|iPhone|iPod|Android/i.test(ua);
    const locale = String(document.documentElement?.lang || navigator.language || '').trim().toLowerCase().replace(/_/g, '-');
    const language = /^[a-z]{2}(?:-|$)/.test(locale) ? locale.slice(0, 2) : null;
    const country = String(window.Shopify?.country || window.OleaAIConfig?.country || '').toUpperCase();
    const themeId = String(window.Shopify?.theme?.id || '');
    const themeRole = String(window.Shopify?.theme?.role || '');
    return { device_type: tablet ? 'tablet' : mobile ? 'mobile' : 'desktop', ...(language ? { language } : {}),
      ...(/^\d{1,30}$/.test(themeId) ? { theme_id: themeId } : {}), ...(['main', 'unpublished', 'development'].includes(themeRole) ? { theme_role: themeRole } : {}),
      preview: PREVIEW, collector_version: VERSION,
      ...(/^[A-Z]{2}$/.test(country) ? { market_country_code: country } : {}) };
  }
  function reportError(code, status = null) {
    diagnostics.last_error = { code, status, at: new Date().toISOString() };
    updateDebugPanel();
    // Only operational codes, never payloads, personal data, tokens or URLs.
    window.console?.warn?.('[Oléa analytics] ' + code + (status ? ' (HTTP ' + status + ')' : ''));
  }
  function status() {
    const privacy = window.Shopify?.customerPrivacy;
    let bannerRequired = null, analyticsChoice = 'unknown';
    try { if (typeof privacy?.shouldShowBanner === 'function') bannerRequired = privacy.shouldShowBanner() === true; } catch {}
    try {
      const choice = privacy?.currentVisitorConsent?.()?.analytics;
      analyticsChoice = choice === 'yes' ? 'accepted' : choice === 'no' ? 'declined' : choice === '' ? 'unset' : 'unknown';
    } catch {}
    return { version: VERSION, consent_allowed: permitted(), consent_api_available: typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function',
      consent_banner_required: bannerRequired, analytics_consent_choice: analyticsChoice,
      design_mode: DESIGN_MODE, preview: PREVIEW, protocol: pageUrl?.protocol || 'unknown', theme_id: String(window.Shopify?.theme?.id || ''),
      theme_role: String(window.Shopify?.theme?.role || ''), queued: queue.length, sending, wishlist_available: typeof window.OleaWishlist?.get === 'function',
      transport_legacy: window.OleaAITransport?.__oleaAnalytics === true, transport_current: window.OleaAssistantTransport?.__oleaAnalytics === true,
      ...diagnostics, last_error: diagnostics.last_error ? { ...diagnostics.last_error } : null };
  }
  const consentTranslations = (() => {
    try { return JSON.parse(document.getElementById('OleaTranslationBridge')?.textContent || '{}'); }
    catch { return {}; }
  })();
  const translatedConsent = (key, fallback) => consentTranslations[key] || fallback;
  function chooseAnalytics(analytics) {
    // Only the two consent interfaces below call this private closure on a click. Shopify
    // owns the consent cookie; passing analytics alone preserves every other purpose.
    if (typeof analytics !== 'boolean' || DESIGN_MODE || consentChoicePending) return;
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.setTrackingConsent !== 'function') {
      consentChoiceMessage = translatedConsent('statistics_1', 'Le module de consentement Shopify est indisponible. Rechargez la page après avoir autorisé ce module.');
      updateDebugPanel(); return;
    }
    const choiceSequence = ++consentChoiceSequence;
    consentChoicePending = true; consentChoiceMessage = translatedConsent('statistics_2', 'Enregistrement de votre choix…'); updateDebugPanel();
    const finish = result => {
      if (!consentChoicePending || choiceSequence !== consentChoiceSequence) return;
      consentChoicePending = false; clearTimeout(timeout);
      consentChoiceMessage = result?.error ? translatedConsent('statistics_3', 'Shopify n’a pas pu enregistrer votre choix. Réessayez.') :
        analytics ? translatedConsent('statistics_4', 'Votre accord pour les statistiques a été enregistré.') : translatedConsent('statistics_5', 'Votre refus des statistiques a été enregistré.');
      consentCheck(); scanCards(); updateDebugPanel();
    };
    const timeout = setTimeout(() => finish({ error: true }), 10000);
    try { privacy.setTrackingConsent({ analytics }, finish); }
    catch { finish({ error: true }); }
  }
  function consentStatusText(current = status()) {
    return consentChoiceMessage || (current.analytics_consent_choice === 'accepted' ? translatedConsent('statistics_6', 'Votre choix actuel : statistiques autorisées.') :
      current.analytics_consent_choice === 'declined' ? translatedConsent('statistics_7', 'Votre choix actuel : statistiques refusées. Aucune nouvelle interaction n’est transmise.') :
        current.consent_api_available ? translatedConsent('statistics_8', 'Vous pouvez autoriser ou refuser les statistiques pour ce navigateur.') :
          translatedConsent('statistics_9', 'Le module de consentement Shopify n’est pas encore disponible. Aucun choix ne sera modifié sans votre action.'));
  }
  function updateConsentDialog() {
    const dialog = document.querySelector?.('#olea-statistics-dialog');
    if (!dialog) return;
    const unavailable = typeof window.Shopify?.customerPrivacy?.setTrackingConsent !== 'function';
    for (const button of dialog.querySelectorAll('[data-olea-statistics-consent]')) button.disabled = consentChoicePending || unavailable || DESIGN_MODE;
    dialog.setAttribute('aria-busy', String(consentChoicePending));
    const state = dialog.querySelector('[data-olea-statistics-status]');
    const message = consentStatusText();
    if (state && state.textContent !== message) state.textContent = message;
  }
  function installPrivacyChoices() {
    if (DESIGN_MODE || !document.body || typeof document.createElement !== 'function') return;
    const footer = document.querySelector('footer.site-footer, .site-footer, footer');
    const links = footer?.querySelector('.footer-policies');
    if (!links) return;
    const place = wrapper => {
      const terms = [...links.querySelectorAll('a')].find(link => /(?:^|\/)policies\/terms-of-service\/?$|(?:^|\/)(?:cgv|conditions-generales(?:-de-vente)?)\/?$/i.test((link.getAttribute('href') || '').split(/[?#]/)[0]) || /^cgv$/i.test(link.textContent.trim()));
      if (terms) { if (terms.nextElementSibling !== wrapper) links.insertBefore(wrapper, terms.nextSibling); }
      else if (wrapper.parentNode !== links) links.append(wrapper);
    };
    const existing = document.querySelector('[data-olea-statistics-choices]');
    if (existing) { place(existing); return; }
    if (!document.querySelector('#olea-statistics-style')) {
      const style = document.createElement('style'); style.id = 'olea-statistics-style';
      style.textContent = '[data-olea-statistics-choices]{display:inline-flex;align-items:baseline;flex-wrap:wrap;max-width:100%;padding:0;text-align:start;font:inherit;color:inherit}[data-olea-statistics-choices]>button{appearance:none;background:none;border:0;color:inherit;padding:0;font:inherit;line-height:inherit;text-decoration:none;text-underline-offset:3px;cursor:pointer}[data-olea-statistics-choices]>button:hover{text-decoration:underline}[data-olea-statistics-choices]>button:focus-visible,#olea-statistics-dialog button:focus-visible{outline:2px solid var(--red,#61241f);outline-offset:3px}[data-olea-statistics-footer-status]{display:block;flex-basis:100%;font-size:12px;line-height:1.5}[data-olea-statistics-footer-status]:empty{display:none}#olea-statistics-dialog{position:fixed;inset:0;margin:auto;width:min(520px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow:auto;border:1px solid var(--line,#e8e6e3);border-radius:20px;padding:24px;background:#fff;color:var(--ink,#1b1716);font-family:var(--font-body,Inter,system-ui,sans-serif);font-size:14px;line-height:1.65;box-shadow:0 18px 80px #0003}#olea-statistics-dialog::backdrop{background:#1b17166b}#olea-statistics-dialog .olea-statistics-heading{display:flex;align-items:start;justify-content:space-between;gap:16px;margin-bottom:16px}#olea-statistics-dialog h2{font-family:inherit;font-size:20px;font-weight:600;line-height:1.3;letter-spacing:-.025em;margin:0}#olea-statistics-dialog p{margin:12px 0}#olea-statistics-dialog .olea-statistics-close{appearance:none;border:1px solid var(--line,#e8e6e3);border-radius:8px;padding:7px 10px;background:#fff;color:inherit;font:inherit;font-size:12px;line-height:1.5;min-height:36px;cursor:pointer}#olea-statistics-dialog .olea-statistics-actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:20px}#olea-statistics-dialog .olea-statistics-actions button{appearance:none;flex:1;min-width:180px;min-height:46px;border:1px solid #bcb7b5;border-radius:12px;padding:10px 13px;background:#fff;color:var(--ink,#1b1716);font:inherit;font-size:14px;font-weight:500;line-height:1.5;cursor:pointer;transition:background .15s,border-color .15s}#olea-statistics-dialog .olea-statistics-actions button:hover{background:var(--panel,#f3f3f4);border-color:var(--body,#4a4648)}#olea-statistics-dialog button:disabled{opacity:.55;cursor:wait}#olea-statistics-dialog [data-olea-statistics-status]{font-size:12px;color:var(--body,#4a4648);min-height:20px;margin-bottom:0}@media(prefers-reduced-motion:reduce){#olea-statistics-dialog button{transition:none}}';
      (document.head || document.body).append(style);
    }
    const wrapper = document.createElement('span'); wrapper.dataset.oleaStatisticsChoices = '';
    const toggle = document.createElement('button'); toggle.type = 'button'; toggle.textContent = translatedConsent('statistics_10', 'Choix des statistiques');
    toggle.setAttribute('aria-haspopup', 'dialog'); toggle.setAttribute('aria-controls', 'olea-statistics-dialog');
    const footerStatus = document.createElement('span'); footerStatus.dataset.oleaStatisticsFooterStatus = '';
    footerStatus.setAttribute('role', 'status'); footerStatus.setAttribute('aria-live', 'polite');
    wrapper.append(toggle, footerStatus); place(wrapper);
    let dialog = document.querySelector('#olea-statistics-dialog');
    if (!dialog) {
      dialog = document.createElement('dialog'); dialog.id = 'olea-statistics-dialog';
      dialog.setAttribute('aria-labelledby', 'olea-statistics-title'); dialog.setAttribute('aria-describedby', 'olea-statistics-description');
      const heading = document.createElement('div'); heading.className = 'olea-statistics-heading';
      const title = document.createElement('h2'); title.id = 'olea-statistics-title'; title.textContent = translatedConsent('statistics_11', 'Vos choix pour les statistiques');
      const close = document.createElement('button'); close.type = 'button'; close.className = 'olea-statistics-close'; close.textContent = translatedConsent('statistics_12', 'Fermer');
      close.setAttribute('aria-label', translatedConsent('statistics_13', 'Fermer les choix des statistiques')); close.addEventListener('click', () => { window.OleaOverlayMotion?.hide(dialog) ?? dialog.close(); });
      heading.append(title, close);
      const description = document.createElement('p'); description.id = 'olea-statistics-description';
      description.textContent = translatedConsent('statistics_14', 'Avec votre accord, nous analysons les pages consultées, les favoris, les questions et les réponses de l’assistant IA pour améliorer nos conseils.');
      const locationInfo = document.createElement('p');
      locationInfo.textContent = translatedConsent('statistics_15', 'Ces statistiques incluent le type d’appareil et une localisation approximative (pays et ville) déduite de l’adresse IP. Il ne s’agit pas de votre adresse postale.');
      const retention = document.createElement('p');
      retention.textContent = translatedConsent('statistics_16', 'L’adresse IP est conservée pendant 30 jours. Les données sont accessibles uniquement à l’équipe Oléa dans son espace privé.');
      const purposes = document.createElement('p');
      purposes.textContent = translatedConsent('statistics_17', 'Vous pouvez revenir sur ce choix à tout moment. Vos choix de publicité et de préférences restent inchangés.');
      const actions = document.createElement('div'); actions.className = 'olea-statistics-actions';
      for (const [analytics, label] of [[true, translatedConsent('statistics_18', 'Autoriser les statistiques')], [false, translatedConsent('statistics_19', 'Refuser les statistiques')]]) {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = label;
        button.dataset.oleaStatisticsConsent = String(analytics); button.addEventListener('click', () => chooseAnalytics(analytics)); actions.append(button);
      }
      const state = document.createElement('p'); state.dataset.oleaStatisticsStatus = ''; state.setAttribute('role', 'status'); state.setAttribute('aria-live', 'polite'); state.setAttribute('aria-atomic', 'true');
      dialog.append(heading, description, locationInfo, retention, purposes, actions, state); document.body.append(dialog);
      dialog.addEventListener('close', () => { if (consentReturnFocus?.isConnected) consentReturnFocus.focus({ preventScroll: true }); consentReturnFocus = null; });
      dialog.addEventListener('cancel', () => { consentChoiceMessage = consentChoicePending ? consentChoiceMessage : ''; });
    }
    toggle.addEventListener('click', () => {
      if (dialog.open) return;
      // A visitor may be using the cart, account or assistant. Never replace it.
      if (document.querySelector('dialog[open]')) { footerStatus.textContent = translatedConsent('statistics_20', 'Fermez la fenêtre ouverte pour gérer les statistiques.'); return; }
      footerStatus.textContent = ''; consentChoiceMessage = ''; consentReturnFocus = toggle; updateConsentDialog();
      try { window.OleaOverlayMotion?.show(dialog) ?? dialog.showModal(); } catch { footerStatus.textContent = translatedConsent('statistics_21', 'Les choix des statistiques ne sont pas disponibles dans ce navigateur.'); consentReturnFocus = null; }
    });
    updateConsentDialog();
  }
  function updateDebugPanel() {
    updateConsentDialog();
    if (pageUrl?.searchParams.get('olea_analytics_debug') !== '1' || !document.body || typeof document.createElement !== 'function') return;
    let panel = document.querySelector('[data-olea-analytics-debug]');
    if (!panel) {
      panel = document.createElement('details'); panel.dataset.oleaAnalyticsDebug = ''; panel.open = true;
      panel.style.cssText = 'position:fixed;bottom:12px;left:12px;z-index:2147483647;background:#fff;color:#151515;max-width:min(360px,calc(100vw - 48px));max-height:70vh;overflow:auto;padding:12px;border:1px solid #aaa;border-radius:8px;font:12px/1.4 monospace;box-shadow:0 2px 12px #0002';
      const title = document.createElement('summary'); title.textContent = 'Diagnostic collecte Oléa';
      const explanation = document.createElement('p');
      explanation.textContent = 'Les statistiques mesurent les favoris, les échanges avec l’IA et l’utilisation du site. Choisissez librement de les autoriser ou de les refuser pour ce navigateur. Les choix marketing et préférences restent inchangés.';
      explanation.style.cssText = 'font:12px/1.5 sans-serif;margin:12px 0';
      const controls = document.createElement('div'); controls.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap';
      for (const [analytics, label] of [[true, 'Autoriser les statistiques'], [false, 'Refuser les statistiques']]) {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = label;
        button.dataset.oleaDebugConsent = String(analytics);
        button.style.cssText = 'flex:1;min-width:130px;padding:9px 10px;border:1px solid #777;border-radius:6px;background:#fff;color:#151515;font:12px/1.3 sans-serif;cursor:pointer';
        button.addEventListener('click', () => chooseAnalytics(analytics)); controls.append(button);
      }
      const consentStatus = document.createElement('p'); consentStatus.dataset.oleaDebugConsentStatus = '';
      consentStatus.setAttribute('role', 'status'); consentStatus.setAttribute('aria-live', 'polite');
      consentStatus.style.cssText = 'font:12px/1.5 sans-serif;margin:10px 0';
      const details = document.createElement('pre'); details.style.cssText = 'font:11px/1.4 monospace;white-space:pre-wrap;overflow-wrap:anywhere';
      panel.append(title, explanation, controls, consentStatus, details); document.body.append(panel);
    }
    const current = status();
    for (const button of panel.querySelectorAll('[data-olea-debug-consent]')) {
      button.disabled = consentChoicePending || typeof window.Shopify?.customerPrivacy?.setTrackingConsent !== 'function' || DESIGN_MODE;
    }
    panel.querySelector('[data-olea-debug-consent-status]').textContent = consentChoiceMessage ||
      (current.consent_allowed ? 'Collecte des statistiques autorisée pour ce navigateur.' :
        current.analytics_consent_choice === 'declined' ? 'Statistiques refusées : aucune nouvelle interaction n’est transmise.' :
          current.consent_api_available ? 'La collecte attend votre accord pour les statistiques.' : 'Le module de consentement Shopify n’est pas encore disponible.');
    panel.querySelector('pre').textContent = JSON.stringify(current, null, 2);
  }
  function enqueue(event_name, data = {}) {
    if (!BROWSER_EVENTS.has(event_name)) return false;
    const current = identity();
    if (!current) return false;
    if (current.fresh && event_name !== 'session_started') { current.fresh = false; enqueue('session_started'); }
    else if (event_name === 'session_started') current.fresh = false;
    const event = { id: uuid(), event_name, occurred_at: new Date(eventClock = Math.max(Date.now(), eventClock + 1)).toISOString(), visitor_id: current.visitor_id, session_id: current.session_id, is_test: false,
      ...data, properties: { source: 'storefront', account_state: cohort(), ...(data.properties || {}), ...commonProperties(), consent_analytics: true } };
    queue.push(event);
    if (['favorite_added', 'favorite_removed'].includes(event_name)) diagnostics.favorite_actions++;
    if (queue.length > 250) queue.shift();
    if (queue.length >= 10) void flush();
    else if (!timer) timer = setTimeout(() => { timer = null; void flush(); }, 250);
    updateDebugPanel();
    return true;
  }
  function flush(options = {}) {
    if (inFlightFlush) return inFlightFlush;
    inFlightFlush = performFlush(options).catch(() => { sending = false; reportError('collection_unexpected_error'); }).finally(() => { inFlightFlush = null; });
    return inFlightFlush;
  }
  async function performFlush({ keepalive = false } = {}) {
    clearTimeout(timer); timer = null;
    if (!permitted()) { queue = []; return; }
    if (sending || !queue.length) return;
    sending = true;
    // When a secure account session exists, resolve its server-side identity
    // before delivering events. Lookup failures leave anonymous analytics usable.
    await verifyCustomerIdentity();
    if (!permitted()) { queue = []; sending = false; return; }
    const batch = await Promise.all(queue.splice(0, 20).map(resolveEventProduct));
    if (!permitted()) { queue = []; sending = false; return; }
    let retry = false;
    try {
      const response = await fetch(ENDPOINT, { method: 'POST', mode: 'cors', credentials: 'omit', keepalive,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ events: batch }), signal: AbortSignal.timeout(5000) });
      let result = null; try { result = await response.json(); } catch {}
      if (!response.ok) {
        reportError('collection_failed', response.status);
        if ((response.status >= 500 || response.status === 429) && permitted()) { queue.unshift(...batch); retry = true; }
      } else {
        const accepted = Number.isInteger(result?.accepted) ? result.accepted : Number.isInteger(result?.inserted) ? result.inserted : batch.length;
        const rejected = Number.isInteger(result?.rejected) ? result.rejected : 0;
        diagnostics.delivered += accepted; diagnostics.rejected += rejected; diagnostics.last_delivery_at = new Date().toISOString();
        if (rejected) reportError('events_rejected', response.status); else diagnostics.last_error = null;
      }
    } catch { reportError('collection_network_error'); if (permitted()) { queue.unshift(...batch); retry = true; } }
    finally {
      sending = false;
      if (queue.length > 250) queue.length = 250;
      if (queue.length && permitted() && !timer) timer = setTimeout(() => { timer = null; void flush(); }, retry ? 5000 : 100);
      updateDebugPanel();
    }
  }
  function pathInfo() {
    const path = location.pathname;
    const product = path.match(/(?:^|\/)products\/([a-z0-9][a-z0-9-]{0,254})\/?$/)?.[1];
    if (product) return { page_type: 'product', path: '/products/' + product, product };
    if (/(?:^|\/)search\/?$/.test(path)) return { page_type: 'search', path: '/search' };
    if (/\/(?:account|customer_authentication|authentication)(?:\/|$)/.test(path)) return { page_type: 'account', path: '/account' };
    if (/\/collections\//.test(path)) return { page_type: 'collection', path: path.replace(/[^a-z0-9/_-]/gi, '').slice(0, 250) };
    return { page_type: path === '/' ? 'home' : 'other', path: path === '/' ? '/' : '/other' };
  }
  function searchResultsCount() {
    const explicit = document.querySelector('[data-search-results-count], [data-results-count]');
    const value = explicit?.dataset.searchResultsCount ?? explicit?.dataset.resultsCount;
    if (typeof value === 'string' && /^\d{1,8}$/.test(value)) return Number(value);
    // Oléa's actual facet toolbar contains only the localized count, without the
    // search terms. Extract its leading number and never retain its text.
    const count = document.querySelector('.search-section [data-result-count], .search-section [data-facet-status], [id^="Search-"] [data-result-count]');
    const digits = count?.textContent?.match(/^\s*([0-9]+(?:[\u00a0\u202f ]+[0-9]{3})*)/)?.[1]?.replace(/[\u00a0\u202f ]/g, '');
    if (digits && /^\d{1,8}$/.test(digits)) return Number(digits);
    if (document.querySelector('.search-section .empty-state[data-results-grid], [id^="Search-"] .empty-state[data-results-grid]')) return 0;
    return null;
  }
  function scanSearchResults() {
    if (!permitted() || emptySearchRecorded || pathInfo().page_type !== 'search') return;
    if (searchResultsCount() === 0) {
      emptySearchRecorded = true;
      enqueue('search_empty', { properties: { page_type: 'search', results_count: 0 } });
    }
  }
  function numericProductId(value) {
    const match = String(value || '').match(/^(?:shopify-|gid:\/\/shopify\/Product\/)?([1-9]\d{0,29})$/);
    return match?.[1] || null;
  }
  async function productForHandle(handle) {
    if (!permitted() || !HANDLE.test(handle || '')) return null;
    if (!productCache.has(handle)) productCache.set(handle, (async () => {
      try {
        const url = new URL('/products/' + encodeURIComponent(handle) + '.js', location.origin);
        if (url.origin !== location.origin) return null;
        const response = await fetch(url.href, { credentials: 'omit', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(2500) });
        if (!response.ok) return null;
        const data = await response.json(), id = numericProductId(data.id);
        return id && data.handle === handle ? { id, title: String(data.title || '').slice(0, 240) } : null;
      } catch { return null; }
    })());
    return productCache.get(handle);
  }
  async function resolveEventProduct(event) {
    if (!event.product_id) return event;
    const id = numericProductId(event.product_id);
    if (id) return { ...event, product_id: id };
    const handle = HANDLE.test(event.properties?.handle || '') ? event.properties.handle : HANDLE.test(event.product_id) ? event.product_id : null;
    const product = handle ? await productForHandle(handle) : null;
    const resolved = { ...event, properties: { ...event.properties, ...(handle ? { handle } : {}) } };
    if (product) { resolved.product_id = product.id; resolved.product_title = event.product_title || product.title; }
    else { delete resolved.product_id; reportError('product_resolution_failed'); } // Unresolved products never poison an event batch.
    return resolved;
  }
  function itemsOf(raw) {
    const items = new Map();
    for (const item of (Array.isArray(raw) ? raw : []).slice(0, 100)) {
      const handle = typeof item === 'string' ? item : item?.handle;
      if (!HANDLE.test(handle || '')) continue;
      const variant = /^\d{1,30}$/.test(String(item?.variantId || '')) ? String(item.variantId) : null;
      items.set(handle, { handle, variant_id: variant });
    }
    return items;
  }
  function snapshot(raw, reason = 'initial') {
    const next = itemsOf(raw);
    baseline = next;
    const snapshotId = uuid();
    enqueue('favorites_snapshot_observed', { properties: { snapshot_id: snapshotId, results_count: next.size, reason } });
    for (const [handle, item] of next) enqueue('favorites_imported', { product_id: handle,
      properties: { reason, snapshot_id: snapshotId, favorite_count: next.size, variant_id: item.variant_id } });
  }
  function installWishlist(store) {
    if (!store || store.__oleaAnalytics) return;
    // These existing methods synchronously write localStorage and emit change.
    // Account syncing uses replace/replaceState; it is never a fresh user add.
    for (const name of ['add', 'remove', 'removeMany', 'clear', 'set', 'replace', 'replaceState', 'importGuest']) {
      const original = store[name];
      if (typeof original !== 'function') continue;
      store[name] = function (...args) {
        const previous = mutationSource;
        mutationSource = ['add', 'remove', 'removeMany', 'clear'].includes(name) ? 'local_action' : name;
        try { return original.apply(this, args); } finally { mutationSource = previous; }
      };
    }
    Object.defineProperty(store, '__oleaAnalytics', { value: true });
  }
  function initializeWishlist(reason = 'initial') {
    const store = window.OleaWishlist;
    if (!store?.get) return;
    installWishlist(store);
    importOwner = String(store.getOwner?.() || store.owner || 'guest');
    snapshot(store.get(), reason);
  }
  function wishlistChange(event) {
    if (!permitted()) { baseline = null; return; }
    installWishlist(window.OleaWishlist);
    const raw = event.detail?.items || event.detail?.handles || [];
    const next = itemsOf(raw);
    const owner = String(event.detail?.owner || window.OleaWishlist?.getOwner?.() || window.OleaWishlist?.owner || 'guest');
    const localAction = mutationSource === 'local_action' || ['add', 'remove'].includes(event.detail?.action);
    if (!baseline || owner !== importOwner) { importOwner = owner; snapshot(raw, 'owner_or_initial'); return; }
    const previous = baseline;
    const changed = [...previous.keys()].some(key => !next.has(key)) || [...next.keys()].some(key => !previous.has(key));
    if (changed && !localAction) {
      for (const [handle, item] of previous) if (!next.has(handle)) enqueue('favorites_snapshot_removed', { product_id: handle,
        properties: { reason: mutationSource || 'observed_state', variant_id: item.variant_id } });
      snapshot(raw, mutationSource || event.detail?.action || 'observed_state'); return;
    }
    baseline = next;
    for (const [handle, item] of next) if (!previous.has(handle)) enqueue('favorite_added', { product_id: handle, properties: { variant_id: item.variant_id } });
    for (const [handle, item] of previous) if (!next.has(handle)) enqueue('favorite_removed', { product_id: handle, properties: { variant_id: item.variant_id } });
    if (changed) void flush();
  }
  function consentCheck({ changed = false } = {}) {
    const allowed = permitted();
    if (!allowed) {
      if (allowedPreviously) forget();
      allowedPreviously = false; updateDebugPanel(); return;
    }
    const firstAllowed = !allowedPreviously; allowedPreviously = true;
    const current = identity();
    if (current?.fresh) { current.fresh = false; enqueue('session_started'); }
    if (firstAllowed || changed) enqueue('consent_updated', { properties: { analytics_allowed: true } });
    if (firstAllowed) void verifyCustomerIdentity();
    if (!pageRecorded) {
      pageRecorded = true;
      const info = pathInfo();
      enqueue('page_viewed', { properties: { page_type: info.page_type, path: info.path } });
      if (info.product) enqueue('product_viewed', { product_id: info.product, properties: { path: info.path } });
      initializeWishlist(firstAllowed ? 'consent_granted' : 'initial');
    }
    scanSearchResults();
    updateDebugPanel();
  }
  function ensurePrivacyApi() {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
      diagnostics.privacy_load_completed = true; privacyLoading = false; return;
    }
    if (typeof window.Shopify?.loadFeatures !== 'function' || diagnostics.privacy_load_attempts >= 3) return;
    const now = Date.now();
    if (privacyLoading && now - lastPrivacyLoad < 10000 || diagnostics.privacy_load_attempts && now - lastPrivacyLoad < 10000) return;
    privacyLoading = true; lastPrivacyLoad = now; diagnostics.privacy_load_attempts++;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
        privacyLoading = false; diagnostics.privacy_load_error = error ? 'load_features_failed' : null;
        diagnostics.privacy_load_completed = !error && typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
        consentCheck(); scanCards(); updateDebugPanel();
      });
    } catch { privacyLoading = false; diagnostics.privacy_load_error = 'load_features_failed'; updateDebugPanel(); }
  }
  function installTransport(transport, family) {
    if (!transport || typeof transport.createClient !== 'function' || transport.__oleaAnalytics) return transport;
    const original = transport.createClient;
    try {
      transport.createClient = function (config, dependencies = {}) {
        if ((family === 'OleaAssistantTransport' || dependencies.freshConversation === true) && permitted()) conversation({ fresh: true });
        const baseFetch = dependencies.fetch || window.fetch.bind(window);
        const target = String(config?.endpoint || '').replace(/\/$/, '') + '/chat';
        const instrumentedFetch = async (url, options) => {
          // Restrict injection to this client's exact chat endpoint. Nothing is
          // forwarded to OpenAI, Shopify OAuth, contact, or unrelated requests.
          if (String(url) === target && options?.method === 'POST' && typeof options.body === 'string') {
            await verifyCustomerIdentity();
            try {
              const body = JSON.parse(options.body), analytics = context();
              if (analytics.allowed) body.analytics = analytics;
              else delete body.analytics;
              options = { ...options, body: JSON.stringify(body) };
              diagnostics.ai_requests++; updateDebugPanel();
            } catch {}
          }
          return baseFetch(url, options);
        };
        const client = original.call(this, config, { ...dependencies, fetch: instrumentedFetch });
        const reset = client.reset;
        if (typeof reset === 'function') client.reset = function (...args) {
          if (permitted()) conversation({ fresh: true });
          return reset.apply(this, args);
        };
        return client;
      };
      Object.defineProperty(transport, '__oleaAnalytics', { value: true });
    } catch { /* An immutable transport still works; only attribution is absent. */ }
    return transport;
  }
  // The IA runtime is lazy-loaded after first opening. Install before its factory
  // is used without polling or replacing the storefront's fetch implementation.
  for (const name of ['OleaAITransport', 'OleaAssistantTransport']) {
    const descriptor = Object.getOwnPropertyDescriptor(window, name);
    if (!descriptor || descriptor.configurable) {
      let transport = installTransport(window[name], name);
      Object.defineProperty(window, name, { configurable: true, enumerable: true,
        get: () => transport, set: value => { transport = installTransport(value, name); } });
    } else installTransport(window[name], name);
  }

  const viewed = new WeakSet(), preparedCards = new WeakSet(), resolvedCards = new WeakMap();
  function cardProduct(node) {
    if (resolvedCards.has(node)) return resolvedCards.get(node);
    if (node?.oaProduct) return node.oaProduct;
    const handle = node?.dataset?.handle || node?.dataset?.productHandle;
    if (!HANDLE.test(handle || '')) return null;
    const title = node.dataset.title || node.querySelector?.('.product-card__title')?.textContent?.trim() || '';
    return { handle, title };
  }
  function prepareCardProduct(node) {
    if (!node || preparedCards.has(node) || !permitted()) return;
    const product = cardProduct(node);
    if (!product || numericProductId(product.backendId) || !HANDLE.test(product.handle || '')) return;
    preparedCards.add(node);
    // Resolve before a product link can navigate away. The same handle cache is
    // shared with normal event delivery, so this does not duplicate lookups.
    void productForHandle(product.handle).then(resolved => {
      if (resolved && permitted()) resolvedCards.set(node, { ...product, backendId: resolved.id, title: product.title || resolved.title });
    });
  }
  const observeCard = node => {
    if (!node || viewed.has(node) || !permitted()) return;
    const product = cardProduct(node);
    const id = String(product?.backendId || product?.handle || '');
    if (!id || id.length > 255) return;
    viewed.add(node);
    enqueue('ai_recommendation_viewed', { conversation_id: conversation(), product_id: id,
      product_title: String(product?.backendFacts?.canonicalName || product?.title || '').slice(0, 240), properties: { handle: HANDLE.test(product?.handle || '') ? product.handle : null } });
  };
  const intersections = window.IntersectionObserver ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { observeCard(entry.target); if (viewed.has(entry.target)) intersections.unobserve(entry.target); }
  }, { threshold: 0.25 }) : null;
  function scanCards() {
    if (!permitted()) return;
    for (const node of document.querySelectorAll('[data-oa-card-item], .assistant-product-grid [data-wishlist-card]')) {
      prepareCardProduct(node);
      if (viewed.has(node)) continue;
      if (intersections) intersections.observe(node);
      // Do not count rendered but invisible cards as viewed without visibility support.
    }
    scanSearchResults();
  }
  document.addEventListener('wishlist:change', wishlistChange);
  document.addEventListener('olea:wishlist:change', wishlistChange);
  document.addEventListener('olea:wishlist:ready', () => { if (permitted() && !baseline) initializeWishlist('legacy_ready'); });
  const rotateIdentity = () => { forget(); consentCheck(); scanCards(); };
  document.addEventListener('wishlist:owner-change', rotateIdentity);
  document.addEventListener('op:api-cleared', rotateIdentity);
  document.addEventListener('op:api-ready', () => { if (permitted()) void verifyCustomerIdentity(); });
  document.addEventListener('submit', event => {
    if (!permitted()) return;
    const form = event.target;
    if (!form || String(form.tagName || '').toLowerCase() !== 'form' || form.matches?.('[data-sort-form]')) return;
    let action; try { action = new URL(form.getAttribute('action') || location.pathname, location.origin); } catch { return; }
    if (action.origin !== location.origin || !/(?:^|\/)search\/?$/.test(action.pathname) || !form.querySelector?.('[name="q"]')) return;
    // Neither the query value, field contents, nor the URL query string leaves
    // the browser. This records submission volume and separately observed zero results.
    enqueue('search_submitted', { properties: { page_type: 'search' } });
  }, true);
  document.addEventListener('visitorConsentCollected', () => { consentCheck({ changed: true }); scanCards(); });
  document.addEventListener('consentTrackingApiLoaded', () => {
    diagnostics.privacy_load_completed = typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
    privacyLoading = false; consentCheck(); scanCards(); updateDebugPanel();
  });
  document.addEventListener('product-ajax:added', event => {
    if (!permitted()) return;
    const product = event.detail?.product || {};
    const variant = String(product.variant_id || product.id || '');
    if (!/^\d{1,30}$/.test(variant)) return;
    enqueue('cart_added', { ...(product.product_id || product.handle ? { product_id: String(product.product_id || product.handle).slice(0, 255) } : {}),
      properties: { variant_id: variant, quantity: Math.min(100, Math.max(1, Number(product.quantity) || 1)) } });
  });
  document.addEventListener('cart:updated', event => {
    if (!permitted()) return;
    const data = event.detail;
    // Oléa V3 emits cart:updated for note, discount and quantity changes too.
    // Only addedItems is a confirmed successful add, never the whole cart.
    if (!Array.isArray(data?.addedItems) || !Array.isArray(data?.items)) return;
    const seen = new Set();
    for (const addition of data.addedItems.slice(0, 100)) {
      const variant = String(addition?.variantId || ''), quantity = Number(addition?.quantity);
      if (!/^\d{1,30}$/.test(variant) || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 100 ||
        typeof addition.key !== 'string' || !addition.key || seen.has(addition.key)) continue;
      const matches = data.items.filter(item => item?.key === addition.key && String(item.variant_id || item.id || '') === variant);
      if (matches.length !== 1) continue;
      seen.add(addition.key);
      const product = matches[0], productId = numericProductId(product.product_id);
      enqueue('cart_added', { ...(productId ? { product_id: productId } : HANDLE.test(product.handle || '') ? { product_id: product.handle } : {}),
        ...(typeof product.product_title === 'string' ? { product_title: product.product_title.slice(0, 240) } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}), properties: { variant_id: variant, quantity } });
    }
    if (seen.size) void flush();
  });
  document.addEventListener('click', event => {
    const voteButton = event.target.closest?.('[data-oa-vote]');
    if (voteButton && permitted()) {
      const body = voteButton.closest('.oa-message-content');
      const turn = body?.oaEntry?.debugPayload?.turnId;
      const vote = voteButton.getAttribute('aria-pressed') === 'true' ? 'cleared' : voteButton.dataset.oaVote;
      if (['up', 'down'].includes(vote)) enqueue('ai_feedback_submitted', { conversation_id: conversation(),
        properties: { vote, ...(UUID.test(turn || '') ? { turn_id: turn } : {}) } });
      return;
    }
    const link = event.target.closest?.('a[href]'), card = link?.closest?.('[data-oa-card-item], .assistant-product-grid [data-wishlist-card]');
    if (!card || !permitted()) return;
    let url; try { url = new URL(link.href, location.origin); } catch { return; }
    if (url.origin !== location.origin || !/\/products\//.test(url.pathname)) return;
    const product = cardProduct(card), id = String(product?.backendId || product?.handle || '');
    if (id && id.length <= 255) {
      enqueue('ai_recommendation_clicked', { conversation_id: conversation(), product_id: id,
        product_title: String(product?.backendFacts?.canonicalName || product?.title || '').slice(0, 240), properties: { handle: HANDLE.test(product?.handle || '') ? product.handle : null } });
      // Do not wait for the regular batch timer when this click leaves the page.
      void flush({ keepalive: true });
    }
  }, true);
  document.addEventListener('visibilitychange', () => { if (document.hidden) void flush({ keepalive: true }); else { consentCheck(); scanCards(); } });
  window.addEventListener('pagehide', () => { void flush({ keepalive: true }); });
  window.OleaAnalytics = Object.freeze({ context, flush, status, version: VERSION });
  const start = () => {
    installPrivacyChoices();
    consentCheck(); scanCards();
    updateDebugPanel();
    ensurePrivacyApi();
    if (window.MutationObserver && document.body) new MutationObserver(() => { installPrivacyChoices(); scanCards(); }).observe(document.body, { childList: true, subtree: true });
    // Customer Privacy may load asynchronously. Its availability never implies consent.
    let checks = 0;
    const poll = setInterval(() => {
      ensurePrivacyApi(); consentCheck(); scanCards();
      if (++checks >= 600 || typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') clearInterval(poll);
    }, 500);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
